import React from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import HighlightText from '@adsk/alloy-react-highlight-text';
import { DocumentEmptyIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import ListView from './components/ListView';
import TreeView from './components/TreeView';

export const DocumentNameCell = ({ rowData, disabledIds, rowIndicator }) => {
  const disabled = disabledIds.includes(rowData.id);

  return (
    <Flex width="100%" data-testid="DocumentNameCell__content">
      {!rowData.folderType ? (
        <DocumentEmptyIcon
          style={{
            flex: 'none',
            marginRight: '8px',
            color: disabled && theme.colors.charcoal500,
          }}
        />
      ) : (
        <span style={{ color: disabled && theme.colors.charcoal500 }}>
          {rowData.entityIcon}
        </span>
      )}
      <Flex
        flexDirection="column"
        overflow="hidden"
        flex="auto"
        justifyContent="center"
      >
        <OverflowTooltip
          autoTipOnEllipsis
          singleLine
          content={rowData.title}
          style={{ maxWidth: '400px', wordBreak: 'break-word' }}
        >
          <HighlightText
            matched={rowData.highlight}
            text={rowData.title}
            style={{
              color: disabled && theme.colors.charcoal500,
            }}
          />
        </OverflowTooltip>
        {!!rowData.displayPath && (
          <Box color="charcoal500" overflow="hidden" marginTop="6px">
            <OverflowTooltip
              autoTipOnEllipsis
              singleLine
              content={rowData.displayPath}
              style={{ maxWidth: '400px', wordBreak: 'break-word' }}
            >
              {rowData.displayPath}
            </OverflowTooltip>
          </Box>
        )}
      </Flex>
      {rowIndicator}
    </Flex>
  );
};

export const CustomTooltipDocumentNameCell = ({
  rowData,
  disabledIds,
  rowIndicator,
}) => {
  const disabled = disabledIds.includes(rowData.id);
  const { tooltipProps } = rowData;
  return (
    <Box
      data-testid="CustomTooltipDocumentNameCell__content"
      display="inline-flex"
    >
      {!rowData.folderType ? (
        <DocumentEmptyIcon
          style={{
            flex: 'none',
            marginRight: '8px',
            color: disabled && theme.colors.charcoal500,
          }}
        />
      ) : (
        <span style={{ color: disabled && theme.colors.charcoal500 }}>
          {rowData.entityIcon}
        </span>
      )}
      <Flex flexDirection="column" justifyContent="center">
        <Tooltip
          singleLine
          style={{
            maxWidth: '400px',
            wordBreak: 'break-word',
          }}
          content={tooltipProps.text}
          placement={tooltipProps.placement}
        >
          <HighlightText
            matched={rowData.highlight}
            text={rowData.title}
            style={{
              color: disabled && theme.colors.charcoal500,
            }}
          />
          {!!rowData.displayPath && (
            <Box marginTop="6px" overflow="hidden" color="charcoal500">
              {rowData.displayPath}
            </Box>
          )}
        </Tooltip>
      </Flex>
      {rowIndicator}
    </Box>
  );
};

DocumentNameCell.propTypes = {
  rowData: PropTypes.object,
  disabledIds: PropTypes.array,
  rowIndicator: PropTypes.node,
};

CustomTooltipDocumentNameCell.propTypes = {
  rowData: PropTypes.object,
  disabledIds: PropTypes.array,
  rowIndicator: PropTypes.node,
};

const DocsCustomDataView = ({ filters = {}, ...rest }) => {
  const projectId = useCurrentProjectId();
  if (filters.searchExpression) {
    return <ListView projectId={projectId} filters={filters} {...rest} />;
  }
  return <TreeView projectId={projectId} {...rest} />;
};

DocsCustomDataView.propTypes = {
  /**
   * Whether we're loading something from BE.
   */
  loading: PropTypes.bool,
  /**
   * Whether had error fetching data from BE.
   */
  error: PropTypes.bool,
  /**
   * Callback to fetch data again.
   */
  onTryAgain: PropTypes.func,
  /**
   * Load the next page of entities.
   */
  onLoadMore: PropTypes.func,
  /**
   * IDs of selected entities.
   */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * List of entities.
   */
  entities: PropTypes.array,
  /**
   * Callback to set Selections.
   * Handler is of shape: (selectedNodes) => *
   */
  setSelection: PropTypes.func,
  /**
   * The currently applied filters.
   */
  filters: PropTypes.object,
  /**
   * Custom props for data views
   */
  dataViewProps: PropTypes.shape({
    dataFilter: PropTypes.func,
    /**
     * Toggle to allow selection of folders
     */
    enableFoldersSelection: PropTypes.bool,
    /**
     * Toggle to allow whole folder selection. This enables all children
     * of a selected folder to be implicitly selected. This should only be
     * set to true when enabledFoldersSelection is true
     */
    allowWholeFolderSelection: PropTypes.bool,
    /**
     * List of implicitly selected IDs due to parent selection. Works in tandem
     * with allowWholeFolderSelection
     */
    implicitlySelectedIds: PropTypes.arrayOf(PropTypes.string),
    /**
     * To update the list of implicitly selected IDs due to parent selection. Worked
     * in tandem with allowWholeFolderSelection
     */
    setImplicitlySelectedIds: PropTypes.func,
    /**
     * Row IDs that should show an indicator
     */
    rowIdsWithIndicator: PropTypes.arrayOf(PropTypes.string),
    /**
     * Indicator component that's to be rendered onto the desired rows
     */
    renderIndicator: PropTypes.elementType,
  }),
  /**
   * callback to add entities to cache. Shape: ({ entities, domain, type }) => *.
   */
  addEntitiesToCache: PropTypes.func,
  /**
   * plugin - the current plugin
   */
  plugin: PropTypes.object,
};

export default DocsCustomDataView;
